/**
 * product.quantity is UI projection — not inventory truth.
 * Prefer stockSnapshots.current when present (sellable asset, aligned with snap.value).
 * Fallback: lots via isLotIncludedInStockMetrics / aggregateLotsMetrics.
 */

import { queueBusinessPayloadWrite } from '../../../infrastructure/storage/businessPayloadWrite';
import {
  ensureLotsArray,
  isLotExpired,
  LOT_STATUS,
  dedupeLotsList,
  startOfLocalDayMs
} from '../../../utils/lotSystem';
import { sameProductId } from '../../../utils/productIdUtils';
import {
  indexLotsByProduct,
  aggregateLotsMetrics,
  lotProductKey
} from '../lots/lotsIndex';

function isNonInventoryCatalogProduct(product) {
  return Boolean(
    product?.productRole === 'restaurant_dish' ||
      product?.isRestaurantDish ||
      product?.trackInventory === false
  );
}

/**
 * Aggregate snapshot rows by product (sum current/value across store+location).
 * @param {Array|object|null} stockSnapshots
 * @returns {Map<string, { current: number, value: number, count: number }>}
 */
export function indexStockSnapshotQtyByProduct(stockSnapshots) {
  const map = new Map();
  const list = Array.isArray(stockSnapshots)
    ? stockSnapshots
    : stockSnapshots && typeof stockSnapshots === 'object'
      ? Object.values(stockSnapshots)
      : [];
  for (const snap of list) {
    if (!snap || snap.productId == null) continue;
    const key = lotProductKey(snap.productId);
    if (!key) continue;
    const prev = map.get(key) || { current: 0, value: 0, count: 0 };
    prev.current += Number(snap.current) || 0;
    prev.value += Number(snap.value) || 0;
    prev.count += 1;
    map.set(key, prev);
  }
  return map;
}

/**
 * UI-Projektion: calculatedQuantity / value aus Lots (wie Stock), ohne product.quantity zu persistieren.
 * When stockSnapshots present and product has snap rows, prefer snap current/value for display.
 * @param {Array} products
 * @param {Array|object} lots
 * @param {{ stockSnapshots?: Array|object|null }} [options]
 * @returns {Array}
 */
export function enrichProductsWithLotQuantityProjection(
  products = [],
  lots = [],
  options = {}
) {
  const list = Array.isArray(products) ? products : [];
  if (list.length === 0) return list;

  const rawLots = Array.isArray(lots) ? lots : Object.values(lots || {});
  const snapByProduct = indexStockSnapshotQtyByProduct(options.stockSnapshots);
  const hasSnaps = snapByProduct.size > 0;

  // No lots → stock is 0. Do NOT pass catalog product.quantity through (stale after
  // achat Excel import that wrote purchases without receiveGoods / lots).
  // Exception: snaps alone can provide qty when lots temporarily empty (rare).
  if (rawLots.length === 0) {
    return list.map((product) => {
      const sortTouchMs =
        Date.parse(product.updatedAt || product.createdAt || 0) || 0;
      if (isNonInventoryCatalogProduct(product)) {
        return {
          ...product,
          calculatedQuantity: 0,
          stockNow: 0,
          hasLots: false,
          lotsCount: 0,
          activeLotsCount: 0,
          _sortTouchMs: sortTouchMs
        };
      }
      const keyId = lotProductKey(product?.id);
      const keyPid =
        product?.productId != null && product.productId !== ''
          ? lotProductKey(product.productId)
          : '';
      const snap =
        (keyId && snapByProduct.get(keyId)) ||
        (keyPid && keyPid !== keyId ? snapByProduct.get(keyPid) : null);
      if (hasSnaps && snap) {
        return {
          ...product,
          calculatedQuantity: snap.current,
          calculatedValue: snap.value,
          calculatedProfit: 0,
          stockNow: snap.current,
          lotsCount: 0,
          activeLotsCount: 0,
          hasLots: false,
          projectionSource: 'stock_snapshot',
          _sortTouchMs: sortTouchMs
        };
      }
      return {
        ...product,
        calculatedQuantity: 0,
        calculatedValue: 0,
        calculatedProfit: 0,
        stockNow: 0,
        lotsCount: 0,
        activeLotsCount: 0,
        hasLots: false,
        _sortTouchMs: sortTouchMs
      };
    });
  }

  // Ghost clones (same logical lot, different ids) must not ×2 Inventaire qty
  const lotsList = dedupeLotsList(rawLots);

  const todayStartMs = startOfLocalDayMs();
  const lotsByProduct = indexLotsByProduct(lotsList);

  const mergeLotsForProduct = (product) => {
    const keyId = lotProductKey(product?.id);
    const keyPid =
      product?.productId != null && product.productId !== ''
        ? lotProductKey(product.productId)
        : '';
    const fromId = keyId ? lotsByProduct.get(keyId) || [] : [];
    const fromPid =
      keyPid && keyPid !== keyId ? lotsByProduct.get(keyPid) || [] : [];
    if (fromPid.length === 0) return fromId;
    const seen = new Set();
    const merged = [];
    for (const lot of fromId.concat(fromPid)) {
      const id = lot?.id != null ? String(lot.id) : null;
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      merged.push(lot);
    }
    return merged;
  };

  return list.map((product) => {
    const sortTouchMs =
      Date.parse(product.updatedAt || product.createdAt || 0) || 0;

    if (isNonInventoryCatalogProduct(product)) {
      return {
        ...product,
        calculatedQuantity: 0,
        stockNow: 0,
        hasLots: false,
        lotsCount: 0,
        activeLotsCount: 0,
        _sortTouchMs: sortTouchMs
      };
    }

    const productLots = mergeLotsForProduct(product);
    const projected = aggregateLotsMetrics(productLots, todayStartMs);

    // Overlay selling price from lots when catalog field is empty (common after Achats)
    let sellingUnitPrice = Number(product.sellingUnitPrice ?? product.sellingPrice ?? 0) || 0;
    if (!(sellingUnitPrice > 0) && productLots.length > 0) {
      const priced = productLots
        .filter((lot) => Number(lot?.sellingUnitPrice) > 0)
        .sort(
          (a, b) =>
            new Date(b.purchaseDate || b.createdAt || 0) -
            new Date(a.purchaseDate || a.createdAt || 0)
        );
      if (priced[0]) {
        sellingUnitPrice = Number(priced[0].sellingUnitPrice) || 0;
      }
    }

    const keyId = lotProductKey(product?.id);
    const keyPid =
      product?.productId != null && product.productId !== ''
        ? lotProductKey(product.productId)
        : '';
    const snap =
      hasSnaps &&
      ((keyId && snapByProduct.get(keyId)) ||
        (keyPid && keyPid !== keyId ? snapByProduct.get(keyPid) : null));

    // Prefer snap qty/value when present (ledger projection accel); keep lot profit/lots counts
    const stockNow = snap ? snap.current : projected.stockNow;
    const value = snap ? snap.value : projected.value;

    return {
      ...product,
      calculatedQuantity: stockNow,
      calculatedValue: value,
      calculatedProfit: projected.potentialProfit,
      stockNow,
      lotsCount: projected.lotsCount,
      activeLotsCount: projected.activeLotsCount,
      hasLots: productLots.length > 0,
      ...(snap ? { projectionSource: 'stock_snapshot' } : {}),
      ...(sellingUnitPrice > 0
        ? { sellingUnitPrice, sellingPrice: sellingUnitPrice }
        : {}),
      _sortTouchMs: sortTouchMs
    };
  });
}

export function isLotCountedInProductQuantity(lot, referenceDate = new Date()) {
  // Same sellable rule as Stock / Inventaire / POS (legacy missing status = active)
  if (!lot || typeof lot !== 'object') return false;
  if (Number(lot.remainingQuantity) <= 0) return false;
  if (lot.status === LOT_STATUS.EXPIRED) return false;
  if (lot.status === LOT_STATUS.SOLD_OUT) return false;
  if (lot.status === LOT_STATUS.TRANSFERRED) return false;
  if (lot.status && lot.status !== LOT_STATUS.ACTIVE) return false;
  if (isLotExpired(lot, referenceDate)) return false;
  return true;
}

/**
 * Summe verkäuflicher Menge aus Lots (alle Standorte).
 */
export function sumActiveLotQuantityForProduct(lots, productId, location = null) {
  const list = dedupeLotsList(
    Array.isArray(lots) ? lots : Object.values(lots || {})
  );
  const locKey = location ? String(location).trim().toLowerCase() : '';
  return list
    .filter((lot) => sameProductId(lot.productId, productId))
    .filter((lot) => {
      if (!locKey) return isLotCountedInProductQuantity(lot);
      const lotLoc = String(lot.location || 'Principal').trim().toLowerCase();
      return lotLoc === locKey && isLotCountedInProductQuantity(lot);
    })
    .reduce((sum, lot) => sum + Number(lot.remainingQuantity || 0), 0);
}

/**
 * Schreibt product.quantity / calculatedQuantity aus Lots (Anzeige-Projektion).
 */
export async function syncProductQuantityProjections(businessId, productIds = null) {
  if (!businessId) return;

  await queueBusinessPayloadWrite(businessId, (parsed) => {
    ensureLotsArray(parsed);
    const products = Array.isArray(parsed.products) ? parsed.products : [];
    const idSet =
      productIds && productIds.length > 0
        ? new Set(productIds.map((id) => String(id)))
        : null;

    for (const product of products) {
      const pid = product.id ?? product.productId;
      if (idSet && !idSet.has(String(pid))) continue;
      const projected = sumActiveLotQuantityForProduct(parsed.lots, pid);
      product.quantity = projected;
      product.calculatedQuantity = projected;
    }
    return parsed;
  }, { authoritativeFields: ['products'] });
}
